import { z } from 'zod';
import { EarnedValue } from './schedule';

/**
 * Экран руководителя (раздел 6.18, АН-01…АН-04): итоги по проектам одной страницей, освоенный объём,
 * отклонения по разделам и мост маржи от плана к прогнозу. Суммы приходят только тем, у кого есть уровень
 * денег: без него поле равно null и цифр нет ни в ответе, ни в подписях.
 */
const Money = z.number().finite().nullable();
const Pct = z.number().finite().nullable();

/** Светофор проекта: считается одной функцией (analytics/calc.ts), пороги — в ANALYTICS_LIMITS */
export const LIGHTS = { ok: 'В норме', warning: 'Внимание', critical: 'Плохо' } as const;
export type Light = keyof typeof LIGHTS;
export const LightSchema = z.enum(['ok', 'warning', 'critical']);

/** Пороги светофора: те же в расчёте на сервере и в подсказке на странице */
export const ANALYTICS_LIMITS = {
  /** SPI и CPI ниже — внимание, ниже второго — плохо */
  indexWarning: 0.95,
  indexCritical: 0.85,
  /** Падение маржи прогноза против плана, п. п. */
  marginDropWarning: 2,
  marginDropCritical: 5,
  /** Просрочка вехи, дней */
  overdueWarning: 1,
  overdueCritical: 14,
} as const;

/**
 * Рейтинг проекта (АН-04): 0–100 из шести показателей с видимыми весами. Показатель без данных в расчёт не входит,
 * остальные пересчитываются на 100 %. Без уровня денег SPI, CPI и маржа не считаются — рейтинг тогда
 * «без финансовой части» и сравнивать его с полным нельзя.
 */
export const RATING_WEIGHTS = {
  milestones: 25,
  lateShare: 10,
  spi: 20,
  cpi: 20,
  margin: 15,
  remarks: 10,
} as const;
export type RatingKey = keyof typeof RATING_WEIGHTS;

export const RATING_TITLES: Record<RatingKey, string> = {
  milestones: 'Отклонение вех от плана',
  lateShare: 'Доля просроченных вех',
  spi: 'Выполнение графика (SPI)',
  cpi: 'Затраты к освоенному (CPI)',
  margin: 'Маржа против плана',
  remarks: 'Просроченные замечания',
};

export const ProjectRating = z.object({
  /** 0–100; null — ни одного показателя посчитать нельзя */
  score: z.number().int().min(0).max(100).nullable(),
  /** Рейтинг посчитан без финансовой части (нет уровня денег или нет данных по деньгам) */
  withoutFinance: z.boolean(),
  parts: z.array(
    z.object({
      key: z.enum(['milestones', 'lateShare', 'spi', 'cpi', 'margin', 'remarks']),
      title: z.string(),
      weight: z.number(),
      /** 0–100; null — данных нет, показатель не учтён */
      score: z.number().nullable(),
      /** Что стоит за оценкой, простыми словами */
      detail: z.string().nullable(),
    }),
  ),
});
export type ProjectRating = z.infer<typeof ProjectRating>;

export const EvSummary = z.object({
  date: z.string(),
  bac: z.number(),
  pv: z.number(),
  ev: z.number(),
  ac: z.number(),
  spi: z.number().nullable(),
  cpi: z.number().nullable(),
  /** Прогноз затрат на завершение по CPI */
  eac: z.number().nullable(),
  finishBaseline: z.string().nullable(),
  finishForecast: z.string().nullable(),
});
export type EvSummary = z.infer<typeof EvSummary>;

export const MarginPoint = z.object({
  revenue: z.number(),
  cost: z.number(),
  margin: z.number(),
  marginPct: Pct,
});
export type MarginPoint = z.infer<typeof MarginPoint>;

export const ManagerProject = z.object({
  id: z.uuid(),
  code: z.string(),
  name: z.string(),
  stage: z.string(),
  managerName: z.string().nullable(),
  light: LightSchema,
  /** Рейтинг 0–100 и из чего он сложился (АН-04) */
  rating: ProjectRating.optional(),
  /** Что именно не в порядке, простыми словами (без сумм) */
  reasons: z.array(z.string()),
  time: z.object({
    overdueMilestones: z.number().int(),
    /** Самое большое отклонение вехи, дней; null — вех нет */
    worstDeviationDays: z.number().nullable(),
    finishBaseline: z.string().nullable(),
    finishForecast: z.string().nullable(),
  }),
  /** null — нет уровня денег */
  money: z
    .object({
      budget: Money,
      planToDate: Money,
      paid: Money,
      /** Освоение бюджета оплатами, % */
      paidPct: Pct,
      planned: MarginPoint.nullable(),
      forecast: MarginPoint.nullable(),
      /** Падение маржи прогноза против плана, п. п. (положительное — маржа упала) */
      marginDropPp: Pct,
      ev: EvSummary.nullable(),
    })
    .nullable(),
});
export type ManagerProject = z.infer<typeof ManagerProject>;

export const ManagerView = z.object({
  asOf: z.string(),
  /** Что видит этот человек: false — суммы скрыты уровнем доступа к деньгам */
  seesMargin: z.boolean(),
  seesEv: z.boolean(),
  totals: z.object({
    projects: z.number().int(),
    critical: z.number().int(),
    warning: z.number().int(),
    overdueMilestones: z.number().int(),
    worstDeviationDays: z.number().nullable(),
    money: z
      .object({
        budget: Money,
        paid: Money,
        paidPct: Pct,
        planned: MarginPoint.nullable(),
        forecast: MarginPoint.nullable(),
        marginDropPp: Pct,
        ev: z
          .object({
            bac: z.number(),
            pv: z.number(),
            ev: z.number(),
            ac: z.number(),
            spi: z.number().nullable(),
            cpi: z.number().nullable(),
          })
          .nullable(),
      })
      .nullable(),
  }),
  projects: z.array(ManagerProject),
  /** Проекты, которые не поместились (показаны первые по коду) */
  more: z.boolean(),
});
export type ManagerView = z.infer<typeof ManagerView>;

/* ─────────── Мост маржи (АН-02) ─────────── */

export const BRIDGE_LINK_TYPES = {
  contract: 'Договор',
  change: 'Запрос на изменение',
  budget_line: 'Раздел бюджета',
  machine: 'Техника',
} as const;
export type BridgeLinkType = keyof typeof BRIDGE_LINK_TYPES;

export const BridgeRow = z.object({
  title: z.string(),
  /** Влияние на маржу: плюс — маржа выросла, минус — упала */
  amount: z.number(),
  detail: z.string().nullable(),
  /** Группа строк внутри шага: для изменений — причина */
  group: z.string().nullable(),
  /** Документ, из которого взято число */
  link: z
    .object({
      type: z.enum(['contract', 'change', 'budget_line', 'machine']),
      id: z.string(),
      path: z.string(),
    })
    .nullable(),
});
export type BridgeRow = z.infer<typeof BridgeRow>;

export const BridgeStep = z.object({
  key: z.enum(['revenue', 'contracts', 'changes']),
  title: z.string(),
  amount: z.number(),
  /** Пояснение: что значит шаг и почему он нулевой */
  note: z.string().nullable(),
  rows: z.array(BridgeRow),
});
export type BridgeStep = z.infer<typeof BridgeStep>;

export const MarginBridge = z.object({
  /** От чего считается прогноз: подписанный доходный договор или плановая себестоимость */
  base: z.enum(['contract', 'planned']),
  planned: MarginPoint,
  forecast: MarginPoint,
  steps: z.array(BridgeStep),
  /** Что осталось неразнесённым между мостом и прогнозом (должно быть около нуля) */
  unexplained: z.number(),
  /** Что в прогноз не входит, но влияет на маржу (справочно): простои техники, ТО, ГСМ */
  outside: z.array(
    z.object({
      key: z.enum(['idle', 'service', 'fuel']),
      title: z.string(),
      /** Деньги; null — цены нет в портале, показан только объём */
      amount: Money,
      volume: z.string().nullable(),
      note: z.string().nullable(),
      rows: z.array(BridgeRow),
    }),
  ),
});
export type MarginBridge = z.infer<typeof MarginBridge>;

/* ─────────── Отклонения по разделам (АН-03) ─────────── */

const Finding = z.object({
  id: z.uuid(),
  severity: z.string(),
  title: z.string(),
  path: z.string().nullable(),
});

export const SectionDeviation = z.object({
  lineId: z.uuid(),
  code: z.string().nullable(),
  name: z.string(),
  sectionCode: z.string().nullable(),
  /** План себестоимости раздела и прогноз (договоры плюс связанные изменения) */
  planCost: z.number(),
  forecastCost: z.number(),
  costDeviation: z.number(),
  costDeviationPct: Pct,
  /** Срок: на сколько дней позже базового плана заканчивается самая поздняя работа раздела ИСР с тем же шифром */
  time: z
    .object({
      slipDays: z.number().nullable(),
      overdueActivities: z.number().int(),
      spi: z.number().nullable(),
      cpi: z.number().nullable(),
    })
    .nullable(),
  /** Причины: от изменений раздела и от договоров */
  reasons: z.array(z.string()),
  changes: z.array(
    z.object({
      id: z.uuid(),
      number: z.string(),
      title: z.string(),
      status: z.string(),
      costDelta: z.number().nullable(),
      daysDelta: z.number().nullable(),
      reason: z.string(),
    }),
  ),
  contracts: z.array(
    z.object({ id: z.uuid(), number: z.string().nullable(), subject: z.string().nullable() }),
  ),
  risks: z.array(Finding),
});
export type SectionDeviation = z.infer<typeof SectionDeviation>;

/**
 * Факт затрат рядом с маржей (СК-22 / БМ-08): склад, начисление площадки, перерасход против нормы.
 * Только при праве видеть маржу; null — нет доступа или нет бюджета.
 */
export const ProjectCostFacts = z.object({
  consumed: z.number().finite(),
  consumedUnpriced: z.number().int(),
  accrued: z.number().finite(),
  overNorm: z.number().finite(),
  overNormLines: z.number().int(),
  overNormUnpriced: z.number().int(),
});
export type ProjectCostFacts = z.infer<typeof ProjectCostFacts>;

export const ProjectAnalytics = z.object({
  projectId: z.uuid(),
  code: z.string(),
  name: z.string(),
  asOf: z.string(),
  seesMargin: z.boolean(),
  seesEv: z.boolean(),
  /** Освоенный объём проекта (АН-01); null — нет прав на деньги или нет базового плана со стоимостью */
  ev: EarnedValue.nullable(),
  evReason: z.string().nullable(),
  bridge: MarginBridge.nullable(),
  sections: z.array(SectionDeviation).nullable(),
  /** Склад, начисление площадки и перерасход против нормы (СК-22) — рядом с мостом маржи */
  costFacts: ProjectCostFacts.nullable(),
  /** Открытые находки проекта: риски для руководителя (тексты без сумм для тех, у кого нет уровня денег) */
  risks: z.array(Finding),
});
export type ProjectAnalytics = z.infer<typeof ProjectAnalytics>;
